import { supabase } from '../lib/supabaseClient';
import {
  ScheduleItem,
  Employee,
  ExamItem,
  ExamResult,
  LearningModule,
  LearningFolder,
  UserRole,
} from '../types/db';

/* =========================
 * SCHEDULES (ACADEMIC)
 * ========================= */

export async function fetchSchedules(branch_id?: string): Promise<ScheduleItem[]> {
  let query = supabase.from('schedules').select('*');
  if (branch_id && branch_id !== 'ALL') query = query.eq('branch_id', branch_id);

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/* =========================
 * TENTOR / TEACHERS
 * ========================= */

export async function fetchTentors(branch_id?: string): Promise<Employee[]> {
  let query = supabase.from('employees').select('*').eq('role', UserRole.TENTOR);

  if (branch_id && branch_id !== 'ALL') query = query.eq('branch_id', branch_id);

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/* =========================
 * EXAMS (ACADEMIC)
 * ========================= */

export async function fetchExams(branch_id?: string): Promise<ExamItem[]> {
  let query = supabase.from('exams').select('*');
  if (branch_id && branch_id !== 'ALL') query = query.eq('branch_id', branch_id);

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

// Added fetchExamResults to resolve import error in ExamManager.tsx
export async function fetchExamResults(branch_id?: string): Promise<ExamResult[]> {
  let query = supabase.from('exam_results').select('*');
  if (branch_id && branch_id !== 'ALL') query = query.eq('branch_id', branch_id);

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function fetchExamResultsByExam(exam_id: string): Promise<ExamResult[]> {
  const { data, error } = await supabase.from('exam_results').select('*').eq('exam_id', exam_id);

  if (error) throw error;
  return data ?? [];
}

export async function fetchExamResultsByStudent(student_id: string): Promise<ExamResult[]> {
  const { data, error } = await supabase
    .from('exam_results')
    .select('*')
    .eq('student_id', student_id);

  if (error) throw error;
  return data ?? [];
}

/* =========================
 * LEARNING MODULES
 * ========================= */

export async function fetchLearningModules(branch_id?: string): Promise<LearningModule[]> {
  // Gunakan .order('uploaded_at') sesuai nama kolom di database Anda
  let query = supabase
    .from('learning_modules')
    .select('*')
    .order('uploaded_at', { ascending: false });

  if (branch_id && branch_id !== 'ALL') {
    query = query.in('branch_id', ['ALL', branch_id]);
  }

  const { data, error } = await query;
  if (error) {
    /* console removed */
    throw error;
  }
  return data ?? [];
}

/* =========================
 * LEARNING FOLDERS (NEW)
 * ========================= */

export async function fetchLearningFolders(branch_id?: string): Promise<LearningFolder[]> {
  let query = supabase
    .from('learning_folders')
    .select('*')
    .eq('is_active', true)
    .order('grade', { ascending: true });
  if (branch_id && branch_id !== 'ALL') {
    query = query.in('branch_id', ['ALL', branch_id]);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function createLearningFolder(
  folder: Omit<LearningFolder, 'id' | 'created_at' | 'updated_at'>
) {
  const { data, error } = await supabase
    .from('learning_folders')
    .insert([
      {
        ...folder,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ])
    .select();

  if (error) throw error;
  return data?.[0];
}

export async function deleteLearningFolder(folder_id: string) {
  const { error } = await supabase.from('learning_folders').delete().eq('id', folder_id);

  if (error) throw error;
}
